import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Spinner } from "reactstrap";
import { createSelector } from "reselect";
import { useSelector } from "react-redux";
import { requestJson } from "../../helpers/api";
import {
  EmpresaDisponible,
  UnidadNegocioDisponible,
  guardarEmpresaActivaId,
  guardarUnidadNegocioActivaId,
  obtenerEmpresaActivaId,
  obtenerEmpresasDisponibles,
  obtenerUnidadNegocioActivaId,
  suscribirseCambioUnidadActiva,
} from "../../utils/empresa";

const AVATAR_COLORS = ["#1f77b4", "#e67e22", "#8e44ad", "#16a085", "#e74c3c", "#2d6a4f", "#a15c5c"];

const getInitial = (name: string) => name?.trim().charAt(0)?.toUpperCase() || "U";

const getColorForName = (name: string) => {
  if (!name) {
    return AVATAR_COLORS[0];
  }
  const hash = Array.from(name).reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const ProfileDropdown = () => {
  const profiledropdownData = createSelector(
    (state: any) => state.Profile,
    (profileState) => profileState.user,
  );
  const user = useSelector(profiledropdownData);

  const [userName, setUserName] = useState("Usuario");
  const [userSubtitle, setUserSubtitle] = useState<string | null>(null);
  const [permisosUsuario, setPermisosUsuario] = useState<string[]>([]);
  const [empresasDisponibles, setEmpresasDisponibles] = useState<EmpresaDisponible[]>([]);
  const [empresaActivaId, setEmpresaActivaId] = useState<number | null>(() => obtenerEmpresaActivaId());
  const [unidadActivaId, setUnidadActivaId] = useState<number | null>(() =>
    obtenerUnidadNegocioActivaId(obtenerEmpresaActivaId()),
  );
  const [unidadesPorEmpresa, setUnidadesPorEmpresa] = useState<
    Record<number, UnidadNegocioDisponible[]>
  >({});
  const [cargandoUnidadesEmpresaId, setCargandoUnidadesEmpresaId] = useState<number | null>(
    null,
  );
  const [unidadError, setUnidadError] = useState<string | null>(null);

  useEffect(() => {
    const authUser = sessionStorage.getItem("authUser");
    if (!authUser) {
      setUserName("Usuario");
      setUserSubtitle(null);
      setPermisosUsuario([]);
      return;
    }
    const obj = JSON.parse(authUser);
    const nombre =
      obj?.usuario?.nombreUsuario || obj?.username || obj?.email || user?.first_name || "Usuario";
    setUserName(nombre);
    const rolPrincipal =
      (obj?.usuario?.roles && obj.usuario.roles[0]) ||
      (user?.roles && user.roles[0]?.role?.role) ||
      null;
    setUserSubtitle(rolPrincipal);
    const permisos: string[] = Array.isArray(obj?.usuario?.permisos)
      ? obj.usuario.permisos
      : [];
    setPermisosUsuario(permisos);
    const locales = obtenerEmpresasDisponibles();
    setEmpresasDisponibles(locales);
    if (locales.length > 0) {
      const almacenada = obtenerEmpresaActivaId();
      const valida = almacenada && locales.some((empresa) => empresa.id === almacenada);
      const preferida = valida ? almacenada! : locales[0].id;
      if (!valida) {
        guardarEmpresaActivaId(preferida);
      }
      setEmpresaActivaId(preferida);
      setUnidadActivaId(obtenerUnidadNegocioActivaId(preferida));
    } else {
      setEmpresaActivaId(obtenerEmpresaActivaId());
      setUnidadActivaId(null);
    }
  }, [user]);

  const cargarEmpresasDesdeApi = useCallback(async () => {
    try {
      const data = await requestJson<any[]>("/api/saas/empresas");
      const normalizadas = (Array.isArray(data) ? data : [])
        .map((item) => {
          const id = Number(item?.id ?? item?.empresaId ?? item?.empresa_id);
          if (!Number.isFinite(id)) return null;
          const nombre =
            item?.nombre ??
            item?.razonSocial ??
            item?.razon_social ??
            item?.descripcion ??
            `Empresa ${id}`;
          return { id, nombre };
        })
        .filter(Boolean) as EmpresaDisponible[];
      if (normalizadas.length === 0) {
        return;
      }
      setEmpresasDisponibles(normalizadas);
      const almacenada = obtenerEmpresaActivaId();
      const valida = almacenada && normalizadas.some((empresa) => empresa.id === almacenada);
      const preferida = valida ? almacenada! : normalizadas[0].id;
      if (!valida) {
        guardarEmpresaActivaId(preferida);
      }
      setEmpresaActivaId(preferida);
    } catch (error) {
      console.error("No pudimos obtener las empresas disponibles.", error);
    }
  }, []);

  useEffect(() => {
    if (empresasDisponibles.length === 0) {
      void cargarEmpresasDesdeApi();
    }
  }, [empresasDisponibles.length, cargarEmpresasDesdeApi]);

  const avatarColor = useMemo(() => getColorForName(userName), [userName]);
  const avatarInitial = useMemo(() => getInitial(userName), [userName]);
  const puedeVerUnidades = useMemo(
    () => permisosUsuario.includes("empresas.unidades.ver"),
    [permisosUsuario],
  );
  const empresaActivaNombre = useMemo(() => {
    if (!empresaActivaId) return null;
    const empresa = empresasDisponibles.find((item) => item.id === empresaActivaId);
    return empresa?.nombre ?? null;
  }, [empresaActivaId, empresasDisponibles]);
  const unidadesEmpresaActiva = useMemo(() => {
    if (!empresaActivaId) return undefined;
    return unidadesPorEmpresa[empresaActivaId];
  }, [empresaActivaId, unidadesPorEmpresa]);
  const unidadActivaNombre = useMemo(() => {
    if (!empresaActivaId || !unidadActivaId) return null;
    const lista = unidadesPorEmpresa[empresaActivaId];
    if (!lista) return null;
    return lista.find((unidad) => unidad.id === unidadActivaId)?.nombre ?? null;
  }, [empresaActivaId, unidadActivaId, unidadesPorEmpresa]);

  const cargarUnidades = useCallback(
    async (empresaId: number) => {
      if (!puedeVerUnidades || !empresaId) return;
      setUnidadError(null);
      setCargandoUnidadesEmpresaId(empresaId);
      try {
        const data = await requestJson<any[]>(
          `/api/empresas/${empresaId}/unidades-negocio`,
        );
        const normalizadas = (Array.isArray(data) ? data : [])
          .map((item) => {
            const id = Number(item?.id);
            if (!Number.isFinite(id)) return null;
            const nombre = item?.nombre ?? item?.descripcion ?? `Unidad ${id}`;
            const codigo = item?.codigo ?? null;
            return { id, nombre, codigo };
          })
          .filter(Boolean) as UnidadNegocioDisponible[];
        setUnidadesPorEmpresa((prev) => ({
          ...prev,
          [empresaId]: normalizadas,
        }));
        if (normalizadas.length === 0) {
          guardarUnidadNegocioActivaId(empresaId, null);
          setUnidadActivaId(null);
          return;
        }
        const almacenada = obtenerUnidadNegocioActivaId(empresaId);
        const valida =
          almacenada && normalizadas.some((unidad) => unidad.id === almacenada);
        const preferida = valida ? almacenada! : normalizadas[0].id;
        if (!valida) {
          guardarUnidadNegocioActivaId(empresaId, preferida);
        }
        setUnidadActivaId(preferida);
      } catch (error) {
        console.error("No pudimos obtener las unidades de negocio.", error);
        setUnidadError("No pudimos cargar las unidades.");
      } finally {
        setCargandoUnidadesEmpresaId(null);
      }
    },
    [puedeVerUnidades],
  );

  useEffect(() => {
    if (!puedeVerUnidades || !empresaActivaId) {
      setUnidadActivaId(null);
      return;
    }
    if (!unidadesEmpresaActiva) {
      void cargarUnidades(empresaActivaId);
      return;
    }
    if (unidadesEmpresaActiva.length === 0) {
      setUnidadActivaId(null);
      return;
    }
    const almacenada = obtenerUnidadNegocioActivaId(empresaActivaId);
    const valida =
      almacenada &&
      unidadesEmpresaActiva.some((unidad) => unidad.id === almacenada);
    const preferida = valida ? almacenada! : unidadesEmpresaActiva[0].id;
    if (!valida) {
      guardarUnidadNegocioActivaId(empresaActivaId, preferida);
    }
    setUnidadActivaId(preferida);
  }, [
    empresaActivaId,
    puedeVerUnidades,
    unidadesEmpresaActiva,
    cargarUnidades,
  ]);

  useEffect(() => {
    if (!empresaActivaId) return;
    const unsubscribe = suscribirseCambioUnidadActiva(({ empresaId, unidadId }) => {
      if (empresaId === empresaActivaId) {
        setUnidadActivaId(unidadId ?? null);
      }
    });
    return unsubscribe;
  }, [empresaActivaId]);

  const [isProfileDropdown, setIsProfileDropdown] = useState(false);
  const toggleProfileDropdown = () => setIsProfileDropdown((prev) => !prev);

  const seleccionarEmpresa = (empresaId: number) => {
    setEmpresaActivaId(empresaId);
    guardarEmpresaActivaId(empresaId);
    setUnidadActivaId(obtenerUnidadNegocioActivaId(empresaId));
    setIsProfileDropdown(false);
  };

  const seleccionarUnidad = (unidadId: number | null) => {
    if (!empresaActivaId) return;
    setUnidadActivaId(unidadId);
    guardarUnidadNegocioActivaId(empresaActivaId, unidadId);
    setIsProfileDropdown(false);
  };

  return (
    <Dropdown
      isOpen={isProfileDropdown}
      toggle={toggleProfileDropdown}
      className="ms-sm-3 header-item topbar-user"
    >
      <DropdownToggle tag="button" type="button" className="btn">
        <span className="d-flex align-items-center">
          <span
            className="rounded-circle header-profile-user d-flex align-items-center justify-content-center text-white fw-semibold"
            style={{ backgroundColor: avatarColor, width: 32, height: 32, textTransform: "uppercase" }}
          >
            {avatarInitial}
          </span>
          <span className="text-start ms-xl-2">
            <span className="d-none d-xl-inline-block ms-1 fw-medium user-name-text">{userName}</span>
            {userSubtitle && (
              <span className="d-none d-xl-block ms-1 fs-12 user-name-sub-text">{userSubtitle}</span>
            )}
          </span>
        </span>
      </DropdownToggle>
      <DropdownMenu className="dropdown-menu-end">
        {empresasDisponibles.length > 0 && (
          <>
            <div className="px-3 py-2 text-muted text-uppercase fs-11">Empresas</div>
            {empresasDisponibles.map((empresa) => (
              <DropdownItem
                key={empresa.id}
                active={empresaActivaId === empresa.id}
                onClick={() => seleccionarEmpresa(empresa.id)}
                className="d-flex justify-content-between align-items-center"
              >
                <span>{empresa.nombre}</span>
                {empresaActivaId === empresa.id && <i className="ri-check-line text-success" />}
              </DropdownItem>
            ))}
            <DropdownItem divider />
          </>
        )}
        {puedeVerUnidades && empresaActivaId && (
          <>
            <div className="px-3 py-2 text-muted text-uppercase fs-11 d-flex align-items-center justify-content-between">
              <span>Unidades de negocio</span>
              {cargandoUnidadesEmpresaId === empresaActivaId && (
                <Spinner size="sm" color="secondary" />
              )}
            </div>
            {unidadError && (
              <div className="px-3 text-danger fs-12 pb-2">{unidadError}</div>
            )}
            {(!unidadesEmpresaActiva ||
              unidadesEmpresaActiva.length === 0) &&
              !unidadError && (
                <div className="px-3 text-muted fs-12 pb-2">
                  No hay unidades configuradas.
                </div>
              )}
            {unidadesEmpresaActiva &&
              unidadesEmpresaActiva.length > 0 && (
                <>
                  <DropdownItem
                    active={unidadActivaId === null}
                    onClick={() => seleccionarUnidad(null)}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <span>Todas las unidades</span>
                    {unidadActivaId === null && (
                      <i className="ri-check-line text-success" />
                    )}
                  </DropdownItem>
                  {unidadesEmpresaActiva.map((unidad) => (
                    <DropdownItem
                      key={unidad.id}
                      active={unidadActivaId === unidad.id}
                      onClick={() => seleccionarUnidad(unidad.id)}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <span>{unidad.nombre}</span>
                      {unidadActivaId === unidad.id && (
                        <i className="ri-check-line text-success" />
                      )}
                    </DropdownItem>
                  ))}
                </>
              )}
            <DropdownItem divider />
          </>
        )}
        <DropdownItem className="p-0">
          <Link to="/logout" className="dropdown-item">
            <i className="mdi mdi-logout text-muted fs-16 align-middle me-1" />
            <span className="align-middle" data-key="t-logout">
              Cerrar sesión
            </span>
          </Link>
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
};

export default ProfileDropdown;
